import React, { useMemo } from "react";
import { Alert, Box, Typography } from "@mui/material";
import type { WidgetRollup } from "@models/Rollup";
import type { BarOption } from "@components/charts/CategoryRollupBuilder";

const MAX_VISIBLE_BARS = 2;

interface CategoryRollupsNoticeProps {
  categoryRollups: WidgetRollup[];
  barOptions: BarOption[];
  variableIdLabelMap?: Record<string, string>;
  labelOverrides?: Record<string, string>;
  itemLabel?: string;
  rollupLabel?: string;
  chartLabel?: string;
}

const CategoryRollupsNotice: React.FC<CategoryRollupsNoticeProps> = ({
  categoryRollups,
  barOptions,
  variableIdLabelMap,
  labelOverrides,
  itemLabel = "bars",
  rollupLabel = "rollup",
  chartLabel = "chart",
}) => {
  const capitalizedItemLabel =
    itemLabel.charAt(0).toUpperCase() + itemLabel.slice(1);
  const idToDisplayLabel = useMemo(() => {
    const map = new Map<string, string>();
    // Seed from variableIdLabelMap so absorbed source bars are resolvable.
    if (variableIdLabelMap) {
      Object.entries(variableIdLabelMap).forEach(([id, originalName]) => {
        map.set(id, labelOverrides?.[originalName] ?? originalName);
      });
    }
    // barOptions override with already-resolved display labels for current bars.
    barOptions.forEach((b) => map.set(b.sourceLabel, b.displayLabel));
    return map;
  }, [barOptions, variableIdLabelMap, labelOverrides]);

  if (categoryRollups.length === 0) return null;

  return (
    <Alert severity="info" sx={{ fontSize: "0.875rem" }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {capitalizedItemLabel} included in {rollupLabel}s
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Some source {itemLabel} are represented by rollup {itemLabel} and are
        not shown separately in this {chartLabel}. Edit or delete a {rollupLabel}{" "}
        to show its source {itemLabel} again.
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {categoryRollups.map((rollup) => {
          const displayLabels = rollup.sourceCategoryLabels.map(
            (id) => idToDisplayLabel.get(id) ?? id,
          );
          const visible = displayLabels.slice(0, MAX_VISIBLE_BARS);
          const overflow = displayLabels.length - MAX_VISIBLE_BARS;
          const summary =
            overflow > 0
              ? `${visible.join(", ")} +${overflow} more`
              : visible.join(", ");
          return (
            <Typography key={rollup.id} variant="body2">
              <strong>{rollup.name ?? rollup.id}</strong>: {summary}
            </Typography>
          );
        })}
      </Box>
    </Alert>
  );
};

export default CategoryRollupsNotice;
